'use client';
import { useCallback, type KeyboardEvent } from 'react';
import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { Button } from '../../../components/styled/button';
import { Text } from '../../../components/styled/text';
import { Textarea } from '../../../components/styled/textarea';
import { useChatFeedback } from './ChatFeedbackProvider';
import { useTranslations } from 'next-intl';

export function InlineFeedbackForm() {
  const t = useTranslations('designSystem.chat.feedbackActions');
  const { inlineFeedback, inlineFeedbackHandlers } = useChatFeedback();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        inlineFeedbackHandlers.onSubmit();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        inlineFeedbackHandlers.onCancel();
      }
    },
    [inlineFeedbackHandlers],
  );

  if (!inlineFeedback.activeTraceId) return null;

  return (
    <Box
      w="full"
      mt={2}
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      p={3}
      bg="bg.subtle"
    >
      <VStack gap={2} w="full" alignItems="flex-start">
        <Text fontSize="xs" color="fg.muted">
          {t('feedbackPrompt')}
        </Text>
        <Textarea
          placeholder={t('feedbackPlaceholder')}
          value={inlineFeedback.text}
          onChange={(e) => inlineFeedbackHandlers.onTextChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          resize="vertical"
          fontSize="xs"
          autoFocus
          disabled={inlineFeedback.isSubmitting}
        />
        {inlineFeedback.submitError && (
          <Text fontSize="xs" color="fg.error">
            {inlineFeedback.submitError}
          </Text>
        )}
        <HStack gap={2} justifyContent="flex-end" w="full">
          <Button
            variant="ghost"
            size="xs"
            onClick={inlineFeedbackHandlers.onCancel}
            disabled={inlineFeedback.isSubmitting}
          >
            {t('cancel')}
          </Button>
          {inlineFeedback.hasPendingRating && (
            <Button
              variant="ghost"
              size="xs"
              onClick={inlineFeedbackHandlers.onSkip}
              disabled={inlineFeedback.isSubmitting}
            >
              {t('skip')}
            </Button>
          )}
          <Button
            variant="outline"
            size="xs"
            onClick={inlineFeedbackHandlers.onSubmit}
            disabled={inlineFeedback.isSubmitting}
          >
            {t('submit')}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
